/**
 * List Sort Store
 *
 * The sort each list page was last left on, remembered on this device per
 * account: the Goals, Plans, Workouts and Challenges lists open where their
 * viewer left them. Choices are keyed by username, so a second account on the
 * device starts from the defaults rather than the first one's picks, and an
 * account that signs back in finds its own.
 *
 * It holds sort keys only, never what the API sent, and resolving a key
 * against its catalog (`resolveSort`) is the platform hook's job, so a sort
 * that has since been renamed falls back to the list's default.
 */

import { persist } from 'zustand/middleware';
import { createStore } from 'zustand/vanilla';

import { createPersistStorage } from './persist-storage';
import { syncGetAccountUsername } from './store-sync';

// =============================================================================
// TYPES
// =============================================================================

/** The lists that remember a sort. */
export type SortedList = 'goals' | 'plans' | 'workouts' | 'challenges';

type ListSorts = Partial<Record<SortedList, string>>;

export interface ListSortStoreState {
  /** Each account's choices, keyed by username. */
  sorts: Record<string, ListSorts>;
}

export interface ListSortStoreActions {
  /** Remember `key` for `list`, for the signed-in account. */
  setSort: (list: SortedList, key: string) => void;
  /** Forget `list`'s choice, so it opens on its default. */
  clearSort: (list: SortedList) => void;
  reset: () => void;
}

export type ListSortStore = ListSortStoreState & ListSortStoreActions;

const STORAGE_KEY = 'personal-trainer-list-sort';

/** Signed out (or in a test without an accessor) the choices share one slot. */
const accountKey = (): string => syncGetAccountUsername() ?? '-';

// =============================================================================
// STORE IMPLEMENTATION
// =============================================================================

export const listSortStore = createStore<ListSortStore>()(
  persist(
    (set) => ({
      sorts: {},

      setSort: (list, key): void => {
        const account = accountKey();
        set((state) => ({
          sorts: {
            ...state.sorts,
            [account]: { ...state.sorts[account], [list]: key },
          },
        }));
      },

      clearSort: (list): void => {
        const account = accountKey();
        set((state) => {
          const { [list]: _cleared, ...rest } = state.sorts[account] ?? {};
          return { sorts: { ...state.sorts, [account]: rest } };
        });
      },

      reset: (): void => {
        set({ sorts: {} });
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createPersistStorage(),
      partialize: (state) => ({ sorts: state.sorts }),
      version: 1,
    }
  )
);

// =============================================================================
// SELECTORS
// =============================================================================

/** The signed-in account's stored key for `list`, unresolved. */
export const selectListSort =
  (list: SortedList) =>
  (state: ListSortStoreState): string | undefined =>
    state.sorts[accountKey()]?.[list];
